import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";

import ConversacionIniciar from "./ConversacionIniciar";
import ConversacionActiva from "./ConversacionActiva";
import MisSesionesList from "./MisSesionesList";
import ResumenView from "./ResumenView";
import Loader from "../../components/UI/Loader";
import Alert from "../../components/UI/Alert";

import {
  getSesionActualThunk,
  getMisSesionesThunk,
  getResumenThunk,
} from "../../store/conversacion/conversacion.thunks";
import { resetConversacion } from "../../store/conversacion/conversacion.slice";

import "./css/ConversacionCSS.css";

const TABS = [
  { key: "inicio", label: "Inicio", icon: "mdi:home-outline" },
  { key: "historial", label: "Historial", icon: "mdi:history" },
];

const ConversacionPage = () => {
  const dispatch = useDispatch();

  const {
    sesionActiva,
    sesionActivaLoading,
    resumen,
    resumenLoading,
    misSesiones,
    misSesionesLoading,
    completando,
    error,
  } = useSelector((state) => state.conversacion);

  const { user } = useSelector((state) => state.auth);

  const [tab, setTab] = useState("inicio");
  const [vista, setVista] = useState("inicio");

  const isMentor = user?.rol?.trim() === "MENTOR";
  const puedeIniciar = isMentor || user?.is_superuser;

  useEffect(() => {
    dispatch(getSesionActualThunk());
    return () => {
      dispatch(resetConversacion());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!sesionActivaLoading) {
      if (sesionActiva && sesionActiva.estado === "en_progreso") {
        setVista("activa");
      }
    }
  }, [sesionActiva, sesionActivaLoading]);

  const handleVerResumen = useCallback(
    (sesionId) => {
      setVista("resumen");
      dispatch(getResumenThunk(sesionId));
    },
    [dispatch],
  );

  const handleTabChange = useCallback(
    (newTab) => {
      setTab(newTab);
      if (newTab === "historial") {
        dispatch(getMisSesionesThunk());
      }
    },
    [dispatch],
  );

  const handleSesionIniciada = useCallback(() => {
    dispatch(getSesionActualThunk());
  }, [dispatch]);

  const handleSesionCompletada = useCallback(() => {
    setVista("inicio");
  }, []);

  if (sesionActivaLoading || completando || resumenLoading) {
    return (
      <div className="card p-24 radius-12">
        <Loader center text="Cargando..." delay={200} />
      </div>
    );
  }

  if (vista === "resumen" && resumen) {
    return (
      <ResumenView
        resumen={resumen}
        onVolver={() => {
          setVista("inicio");
          setTab("inicio");
        }}
      />
    );
  }

  if (vista === "activa" && sesionActiva) {
    return (
      <ConversacionActiva
        sesion={sesionActiva}
        onCompletada={handleSesionCompletada}
        onVerResumen={handleVerResumen}
      />
    );
  }

  return (
    <div className="conversacion-page">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <div className="conversacion-icon-wrapper bg-primary-subtle">
            <Icon icon="mdi:forum" width="24" className="text-primary" />
          </div>
          <div>
            <h4 className="mb-0">Conversación de Talentos</h4>
            <small className="text-secondary">
              Conduce conversaciones guiadas con tus estudiantes
            </small>
          </div>
        </div>
        <div className="conversacion-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`conversacion-tab ${tab === t.key ? "active" : ""}`}
              onClick={() => handleTabChange(t.key)}
            >
              <Icon icon={t.icon} width="18" />
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <Alert
          message={error}
          variant="error"
          onClose={() => dispatch({ type: "conversacion/clearError" })}
          className="mb-3"
        />
      )}

      {tab === "inicio" && (
        <ConversacionIniciar
          puedeIniciar={puedeIniciar}
          onSesionIniciada={handleSesionIniciada}
          sesionActiva={sesionActiva}
        />
      )}

      {tab === "historial" && (
        <>
          {misSesionesLoading ? (
            <Loader center text="Cargando historial..." delay={200} />
          ) : (
            <MisSesionesList
              sesiones={misSesiones}
              onVerResumen={handleVerResumen}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ConversacionPage;
